import { gql } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

/*
学習パス v2 の読み口 (apps/api-go/graph/path_v2.graphqls) を呼ぶ関数と型。
形は読み口の約束 (2026-10-20) のまま。人ごとの答えなので、ここでも呼ぶ側でも覚えない (KV にもチームの共有の cache にも置かない)
*/

export type PathOrderSet = "V1" | "V2";

export interface PathV2Summary {
  pathId: string;
  displayOrder: number;
  name: string;
  summary: string | null;
  goal: string | null;
  entryText: string | null;
  backText: string | null;
  nextText: string | null;
  outcomes: string[];
  learnMinutes: number | null;
  /** パスの中で公開かつ必須の行のうち済みの数 (前に済んだ物は入れない) */
  doneCount: number;
  /** 管理者の見方だけで使う。受講生の画面に出さない */
  totalCount: number;
  /** 次はここの num (api-go の pathv2.NextOf)。必修が全部済んだら null */
  nextNum: number | null;
  /** 残っている物。進んだ所より手前で済んでいない必修 (並びの順) */
  skipped: PathV2Skipped[];
}

export interface PathV2Skipped {
  num: number;
  curriculumId: string;
  title: string;
  itemType: string;
}

export interface PathV2PriorDone {
  curriculumId: string;
  title: string;
  itemType: string;
  completedAt: string | null;
}

export interface PathV2Catalog {
  orderSet: PathOrderSet;
  paths: PathV2Summary[];
  doneCount: number;
  priorDoneCount: number;
  priorDone: PathV2PriorDone[];
}

export interface PathV2Chapter {
  chapterKey: string;
  displayOrder: number;
  title: string;
  goal: string | null;
  /** chapter か stage */
  kind: string;
  icon: string | null;
  makes: string[];
}

export interface PathV2Entry {
  num: number;
  slug: string;
  curriculumId: string;
  chapterKey: string | null;
  title: string;
  description: string | null;
  itemType: string;
  required: boolean;
  /** step、guide、period */
  unit: string;
  /** manual、lessons、quiz */
  completion: string;
  groupKey: string | null;
  groupTitle: string | null;
  partOf: string | null;
  part: number | null;
  done: boolean;
  completedAt: string | null;
  doneInV1: boolean;
  minutes: number | null;
  stars: number | null;
  guide: boolean;
  dojo: boolean;
  tag: string | null;
  doneWhen: string | null;
  lessonType: string | null;
  imageUrl: string | null;
  actionLabel: string | null;
  actionHref: string | null;
}

export interface PathV2Path {
  orderSet: PathOrderSet;
  path: PathV2Summary | null;
  chapters: PathV2Chapter[];
  entries: PathV2Entry[];
}

export interface PathV2NavLink {
  pathId: string;
  num: number;
  title: string;
}

export interface PathV2Item {
  orderSet: PathOrderSet;
  entry: PathV2Entry | null;
  content: string | null;
  assessmentCurriculumId: string | null;
  lessonIds: string[];
  miniQuizJson: string | null;
  /** 見ている人が assessmentCurriculumId のミニテストに合格した試行を持つか。getPathV2Item は読まない (getPathV2MiniTestPassed で別に読む) */
  miniTestPassed?: boolean;
  assignmentId: string | null;
  prev: PathV2NavLink | null;
  next: PathV2NavLink | null;
}

export interface PathV2Location {
  pathId: string;
  num: number;
}

const PATH_V2_SUMMARY_FIELDS = gql`
  fragment PathV2SummaryFields on PathV2Summary {
    pathId
    displayOrder
    name
    summary
    goal
    entryText
    backText
    nextText
    outcomes
    learnMinutes
    doneCount
    totalCount
    nextNum
    skipped {
      num
      curriculumId
      title
      itemType
    }
  }
`;

const PATH_V2_ENTRY_FIELDS = gql`
  fragment PathV2EntryFields on PathV2Entry {
    num
    slug
    curriculumId
    chapterKey
    title
    description
    itemType
    required
    unit
    completion
    groupKey
    groupTitle
    partOf
    part
    done
    completedAt
    doneInV1
    minutes
    stars
    guide
    dojo
    tag
    doneWhen
    lessonType
    imageUrl
    actionLabel
    actionHref
  }
`;

export const VIEWER_PATH_ORDER_SET_QUERY = gql`
  query ViewerPathOrderSet($teamId: String!) {
    viewerPathOrderSet(teamId: $teamId)
  }
`;

export const PATH_V2_CATALOG_QUERY = gql`
  ${PATH_V2_SUMMARY_FIELDS}
  query PathV2Catalog($teamId: String!, $orderSet: PathOrderSet) {
    pathV2Catalog(teamId: $teamId, orderSet: $orderSet) {
      orderSet
      paths {
        ...PathV2SummaryFields
      }
      doneCount
      priorDoneCount
      priorDone {
        curriculumId
        title
        itemType
        completedAt
      }
    }
  }
`;

export const PATH_V2_PATH_QUERY = gql`
  ${PATH_V2_SUMMARY_FIELDS}
  ${PATH_V2_ENTRY_FIELDS}
  query PathV2Path($teamId: String!, $pathId: String!, $orderSet: PathOrderSet) {
    pathV2Path(teamId: $teamId, pathId: $pathId, orderSet: $orderSet) {
      orderSet
      path {
        ...PathV2SummaryFields
      }
      chapters {
        chapterKey
        displayOrder
        title
        goal
        kind
        icon
        makes
      }
      entries {
        ...PathV2EntryFields
      }
    }
  }
`;

export const PATH_V2_ITEM_QUERY = gql`
  ${PATH_V2_ENTRY_FIELDS}
  query PathV2Item($teamId: String!, $pathId: String!, $num: Int!, $orderSet: PathOrderSet) {
    pathV2Item(teamId: $teamId, pathId: $pathId, num: $num, orderSet: $orderSet) {
      orderSet
      entry {
        ...PathV2EntryFields
      }
      content
      assessmentCurriculumId
      lessonIds
      miniQuizJson
      assignmentId
      prev {
        pathId
        num
        title
      }
      next {
        pathId
        num
        title
      }
    }
  }
`;

export const PATH_V2_LOCATE_QUERY = gql`
  query PathV2Locate($teamId: String!, $curriculumId: String!) {
    pathV2Locate(teamId: $teamId, curriculumId: $curriculumId) {
      pathId
      num
    }
  }
`;

export async function getViewerPathOrderSet(
  teamId: string,
  options: GraphqlRequestConfig = {},
): Promise<PathOrderSet> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ viewerPathOrderSet: PathOrderSet }>(
    VIEWER_PATH_ORDER_SET_QUERY,
    { teamId },
  );
  return data.viewerPathOrderSet;
}

export async function getPathV2Catalog(
  teamId: string,
  orderSet: PathOrderSet | null = null,
  options: GraphqlRequestConfig = {},
): Promise<PathV2Catalog> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ pathV2Catalog: PathV2Catalog }>(
    PATH_V2_CATALOG_QUERY,
    { teamId, orderSet },
  );
  const catalog = data.pathV2Catalog;
  return {
    ...catalog,
    paths: catalog.paths ?? [],
    priorDone: catalog.priorDone ?? [],
  };
}

export async function getPathV2Path(
  teamId: string,
  pathId: string,
  orderSet: PathOrderSet | null = null,
  options: GraphqlRequestConfig = {},
): Promise<PathV2Path> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ pathV2Path: PathV2Path }>(
    PATH_V2_PATH_QUERY,
    { teamId, pathId, orderSet },
  );
  const path = data.pathV2Path;
  return {
    ...path,
    chapters: path.chapters ?? [],
    entries: path.entries ?? [],
  };
}

export async function getPathV2Item(
  teamId: string,
  pathId: string,
  num: number,
  orderSet: PathOrderSet | null = null,
  options: GraphqlRequestConfig = {},
): Promise<PathV2Item> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ pathV2Item: PathV2Item }>(
    PATH_V2_ITEM_QUERY,
    { teamId, pathId, num, orderSet },
  );
  const item = data.pathV2Item;
  return { ...item, lessonIds: item.lessonIds ?? [] };
}

const PATH_V2_MINI_TEST_PASSED_QUERY = gql`
  query PathV2ItemMiniTestPassed($teamId: String!, $pathId: String!, $num: Int!, $orderSet: PathOrderSet) {
    pathV2Item(teamId: $teamId, pathId: $pathId, num: $num, orderSet: $orderSet) {
      miniTestPassed
    }
  }
`;

/**
 * 項目のミニテスト (記録の残る物) に、見ている人が合格しているか。項目の頁の「済みにする」の欄が使う。
 * 項目の本文の問い合わせと分けるのは、この欄を持たない api-go (配備の前後) でも本文の頁を落とさないため。呼び手は失敗を false にする
 */
export async function getPathV2MiniTestPassed(
  teamId: string,
  pathId: string,
  num: number,
  orderSet: PathOrderSet | null = null,
  options: GraphqlRequestConfig = {},
): Promise<boolean> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ pathV2Item: { miniTestPassed?: boolean | null } }>(PATH_V2_MINI_TEST_PASSED_QUERY, { teamId, pathId, num, orderSet });
  return data.pathV2Item?.miniTestPassed === true;
}

export async function getPathV2Locate(
  teamId: string,
  curriculumId: string,
  options: GraphqlRequestConfig = {},
): Promise<PathV2Location | null> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ pathV2Locate: PathV2Location | null }>(
    PATH_V2_LOCATE_QUERY,
    { teamId, curriculumId },
  );
  return data.pathV2Locate ?? null;
}

export const SET_VIEWER_PATH_ORDER_SET_MUTATION = gql`
  mutation SetViewerPathOrderSet($teamId: String!, $orderSet: PathOrderSet!) {
    setViewerPathOrderSet(teamId: $teamId, orderSet: $orderSet)
  }
`;

export const VIEWER_PATH_OPT_IN_QUERY = gql`
  query ViewerPathOptIn($teamId: String!) {
    viewerPathOptIn(teamId: $teamId)
  }
`;

/**
 * 本人が URL (/luagate/path?v=2、?v=1) で v2 を見るかを選ぶ。書くのは呼んだ本人の行だけ (代表 10/22)。
 * V2 は v1 の済みを写してから v2 に、V1 は本人の行を消してチームの既定に戻す。体験の人は何も書かない。答えはそのあとの並び
 */
export async function setViewerPathOrderSet(
  teamId: string,
  orderSet: PathOrderSet,
  options: GraphqlRequestConfig = {},
): Promise<PathOrderSet> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ setViewerPathOrderSet: PathOrderSet }>(
    SET_VIEWER_PATH_ORDER_SET_MUTATION,
    { teamId, orderSet },
  );
  return data.setViewerPathOrderSet;
}

/** 本人の行で v2 を見ていて、チームの既定は v2 でないか (学習パスの一覧に「前の学習パスに戻す」を出すか) */
export async function getViewerPathOptIn(
  teamId: string,
  options: GraphqlRequestConfig = {},
): Promise<boolean> {
  const client = createGraphqlClient(options);
  const data = await client.request<{ viewerPathOptIn: boolean }>(
    VIEWER_PATH_OPT_IN_QUERY,
    { teamId },
  );
  return data.viewerPathOptIn;
}
